import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Flame, TrendingDown, ArrowRight } from "lucide-react";

type Drop = {
  brand: string;
  model: string;
  slug: string;
  image_url: string | null;
  price: number;
  previous: number;
  pct: number;
  href: string;
};

const TABLES: Array<{ name: any; route: string }> = [
  { name: "lawn_mowers", route: "/lawn-mowers" },
  { name: "generators", route: "/generators" },
  { name: "chainsaws", route: "/chainsaws" },
  { name: "pressure_washers", route: "/pressure-washers" },
];

async function fetchDrops(limit: number): Promise<Drop[]> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const all: Drop[] = [];

  for (const t of TABLES) {
    const { data } = await supabase
      .from(t.name)
      .select("brand, model, slug, image_url, price_gbp, previous_price_gbp, price_last_checked")
      .eq("status", "published")
      .not("previous_price_gbp", "is", null)
      .not("price_gbp", "is", null)
      .gte("price_last_checked", since)
      .limit(20);

    (data || []).forEach((p: any) => {
      if (p.price_gbp && p.previous_price_gbp && p.previous_price_gbp > p.price_gbp) {
        const pct = ((p.previous_price_gbp - p.price_gbp) / p.previous_price_gbp) * 100;
        if (pct >= 3) {
          all.push({
            brand: p.brand,
            model: p.model,
            slug: p.slug,
            image_url: p.image_url,
            price: Number(p.price_gbp),
            previous: Number(p.previous_price_gbp),
            pct,
            href: `${t.route}/${p.slug}`,
          });
        }
      }
    });
  }

  return all.sort((a, b) => b.pct - a.pct).slice(0, limit);
}

interface PriceDropsWidgetProps {
  variant?: "sidebar" | "homepage";
  limit?: number;
}

export function PriceDropsWidget({ variant = "sidebar", limit }: PriceDropsWidgetProps) {
  const max = limit ?? (variant === "homepage" ? 6 : 4);
  const { data: drops = [], isLoading } = useQuery({
    queryKey: ["price-drops-24h", max],
    queryFn: () => fetchDrops(max),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || drops.length === 0) return null;

  if (variant === "homepage") {
    return (
      <section className="container mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-2 text-foreground">
              <Flame className="h-7 w-7 text-[#16a34a]" />
              Deal of the Day
            </h2>
            <p className="text-muted-foreground text-sm mt-1">Biggest price drops across our catalogue in the last 24 hours.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {drops.map((d) => (
            <Link
              key={d.href}
              to={d.href}
              className="group block bg-white rounded-lg border hover:shadow-lg transition overflow-hidden"
            >
              <div className="relative aspect-square bg-muted/30 flex items-center justify-center">
                {d.image_url && (
                  <img src={d.image_url} alt={`${d.brand} ${d.model}`} className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform" loading="lazy" />
                )}
                <Badge className="absolute top-2 left-2 bg-[#16a34a] text-white hover:bg-[#16a34a] gap-1">
                  <TrendingDown className="h-3 w-3" />-{Math.round(d.pct)}%
                </Badge>
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide truncate">{d.brand}</p>
                <p className="text-sm font-semibold text-foreground line-clamp-2 min-h-[2.5rem]">{d.model}</p>
                <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
                  <span className="font-bold text-foreground">£{d.price.toLocaleString()}</span>
                  <span className="text-xs text-muted-foreground line-through">£{d.previous.toLocaleString()}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  return (
    <Card className="bg-white border-2 border-[#16a34a]/30">
      <CardContent className="p-4">
        <h3 className="font-bold text-foreground flex items-center gap-2 mb-3">
          <Flame className="h-5 w-5 text-[#16a34a]" />
          Today's Price Drops
        </h3>
        <ul className="space-y-3">
          {drops.map((d) => (
            <li key={d.href}>
              <Link to={d.href} className="flex gap-3 group items-start">
                {d.image_url && (
                  <img src={d.image_url} alt={`${d.brand} ${d.model}`} className="w-14 h-14 object-contain rounded bg-muted/30 shrink-0" loading="lazy" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground line-clamp-2 group-hover:text-[#16a34a]">
                    {d.brand} {d.model}
                  </p>
                  <div className="flex items-baseline gap-1.5 mt-1 flex-wrap">
                    <span className="font-bold text-sm text-foreground">£{d.price.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground line-through">£{d.previous.toLocaleString()}</span>
                    <Badge className="bg-[#16a34a] text-white hover:bg-[#16a34a] text-[10px] py-0 px-1.5">-{Math.round(d.pct)}%</Badge>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        <Link to="/best-lawn-mowers-uk" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[#16a34a] hover:underline">
          See all deals <ArrowRight className="h-3 w-3" />
        </Link>
      </CardContent>
    </Card>
  );
}
